import * as React from "react";
import { styled, useTheme, alpha } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Chip from "@mui/material/Chip";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Popover from "@mui/material/Popover";
import InputBase from "@mui/material/InputBase";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import axiosInstance from "../../services/axiosInstance";
import {
  ChevronLeft, ChevronRight, LayoutDashboard, GitBranch, User, LogOut, Phone, BarChart3, Users, Moon, Sun, Ticket, Shield, Headset, CreditCard as CreditCardIcon, ArrowLeftRight, Voicemail, PhoneCall, Tag, Bell, PhoneMissed, ArrowRight, Gauge, Monitor, Mail, Settings, HelpCircle, Search, ChevronDown, Send, MessageSquare, Brain, BookOpen, Bot, Zap, CalendarRange, ClipboardList, SlidersHorizontal, Package, Blocks, MessagesSquare,
} from "lucide-react";
import Header from "../header/Header";
import { LanguageSwitcherCompact } from "../../i18n/LanguageSwitcher.jsx";
import { usePersistLanguage } from "../../i18n/usePersistLanguage.js";
import { useTranslation } from "react-i18next";
import useUnseenOrders from "../pages/ai/useUnseenOrders";
import useWaitingHandoffs from "../pages/handoff/useWaitingHandoffs";
// The business's homes (Stage 4.2b O1b step 7): a screen whose home is switched off is not offered.
import useBusiness from "../pages/ai/useBusiness";
import { VIEW_MODULE, viewAllowed } from "../pages/ai/businessModules";
import ModuleOff from "../pages/ai/ModuleOff";
// ── Eager: needed on first render for both account types ─────────────────────
import Overview      from "../pages/dashboard/Overview";
import AgentDashboard from "../pages/agent/AgentDashboard";
import Profile        from "../pages/profile/profile";
import SetupWizard    from "../pages/createplan/SetupWizard";
// ── Lazy: loaded on first navigation to that view ────────────────────────────
const Analytics     = React.lazy(() => import("../pages/analytics/Analytics"));
const Representatives = React.lazy(() => import("../pages/representatives/Representatives"));
const CRM           = React.lazy(() => import("../pages/crm/CRM"));
const AgentsPage    = React.lazy(() => import("../pages/agents/AgentsPage"));
const Billing       = React.lazy(() => import("../pages/billing/Billing"));
const TransferLogs  = React.lazy(() => import("../pages/transferlogs/TransferLogs"));
const PhoneNumbers  = React.lazy(() => import("../pages/phonenumbers/PhoneNumbers"));
const VoicemailInbox    = React.lazy(() => import("../pages/voicemail/VoicemailInbox"));
const CallHistory       = React.lazy(() => import("../pages/callhistory/CallHistory"));
const Dispositions      = React.lazy(() => import("../pages/dispositions/Dispositions"));
const AgentPerformance  = React.lazy(() => import("../pages/performance/AgentPerformance"));
const EmailReports      = React.lazy(() => import("../pages/emailreports/EmailReports"));
const FlowManagerPage   = React.lazy(() => import("../pages/flowbuilder/FlowManagerPage"));
const WhatsAppPage      = React.lazy(() => import("../pages/whatsapp/WhatsAppPage"));
const AiUsage           = React.lazy(() => import("../pages/ai/AiUsage"));
const KnowledgeBase     = React.lazy(() => import("../pages/ai/KnowledgeBase"));
const AgentPersona      = React.lazy(() => import("../pages/ai/AgentPersona"));
const AiActions         = React.lazy(() => import("../pages/ai/AiActions"));
const AiCalendar        = React.lazy(() => import("../pages/ai/AiCalendar"));
const OrdersBoard       = React.lazy(() => import("../pages/ai/OrdersBoard"));
const BusinessSettings  = React.lazy(() => import("../pages/ai/BusinessSettings"));
const OrderSettings     = React.lazy(() => import("../pages/ai/OrderSettings"));
const CatalogPage       = React.lazy(() => import("../pages/ai/CatalogPage"));
import { useDispatch, useSelector } from "react-redux";
import { logout, fetchMe, fetchAgentPerms, selectAgentPerms } from "../../actions/authSlice";
import { fetchLiveSummary } from "../pages/analytics/analyticsSlice";
import { fetchOpenCount } from "../pages/crm/crmSlice";
import { hideToast, showToast } from "../../actions/toastSlice";
import { unregisterSIP } from "../phone/usePhoneLine";
import PhoneProvider from "../phone/PhoneProvider";
import PhoneTopButton from "../phone/PhoneTopButton";
import { getProfile, setLocaleName } from "../pages/createplan/ProfileSlice";
import { setFlowModeLocal } from "../pages/flowbuilder/flowBuilderSlice";
import { useNavigate } from "react-router-dom";
import { useColorMode } from "../../theme/ColorModeContext";
import glyphImg from "../../../design-system/assets/brand/glyph.png";
import ChatWidget from "./ChatWidget";
import BillingBanner from "../pages/billing/BillingBanner";

function PageFallback() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 320 }}>
      <CircularProgress size={28} sx={{ color: "primary.main", opacity: 0.5 }} />
    </Box>
  );
}

// Catches dynamic-import failures (stale chunk after deploy) and reloads once.
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { errored: false, forView: props.resetKey };
  }
  // Clear the error when the user navigates elsewhere. Without this one throw
  // pins every subsequent view to the error screen, making a single broken
  // page look like the whole app is down.
  static getDerivedStateFromProps(props, state) {
    if (props.resetKey !== state.forView) {
      return { errored: false, forView: props.resetKey };
    }
    return null;
  }
  static getDerivedStateFromError(err) {
    const isChunkErr =
      err?.name === "ChunkLoadError" ||
      /Failed to fetch dynamically imported module|Loading chunk|Loading CSS chunk/.test(err?.message || "");
    if (isChunkErr) {
      const key = "chunk_reload_attempted";
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        window.location.reload();
        return null; // stay alive while reload happens
      }
    }
    return { errored: true };
  }
  componentDidCatch(err, info) {
    console.error("[ChunkErrorBoundary]", err, info);
  }
  render() {
    if (this.state.errored) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 320, gap: 2, p: 4 }}>
          <Typography sx={{ fontWeight: 700, color: "var(--ink-900)", fontFamily: "var(--font-display)", fontSize: "18px" }}>
            Something went wrong loading this page.
          </Typography>
          <Typography sx={{ color: "var(--ink-500)", fontFamily: "var(--font-ui)", fontSize: "14px", textAlign: "center" }}>
            A new version of the app may have been deployed. Try refreshing.
          </Typography>
          <Box component="button"
            onClick={() => { sessionStorage.removeItem("chunk_reload_attempted"); window.location.reload(); }}
            sx={{ mt: 1, px: 3, py: 1, borderRadius: "10px", border: "none", cursor: "pointer", fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: "14px", background: "var(--grad-brand-135)", color: "#fff" }}>
            Refresh page
          </Box>
        </Box>
      );
    }
    return this.props.children;
  }
}

const DRAWER_WIDTH     = 256;
const DRAWER_COLLAPSED = 72;
const BRAND_GRADIENT   = "var(--grad-brand-135)";
// Sidebar always dark indigo per design spec — it is the only dark surface
const SIDEBAR_BG       = "linear-gradient(180deg, var(--nav-bg) 0%, var(--nav-bg-deep) 100%)";

const openedMixin = (theme) => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: DRAWER_COLLAPSED,
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (p) => p !== "open",
})(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  background: "var(--surface-card)",
  color: "var(--ink-800)",
  borderBottom: "1px solid var(--line-100)",
  boxShadow: "0 1px 0 var(--line-100)",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  // Collapsed (mini) baseline — always offset by mini width on md+
  [theme.breakpoints.up("md")]: {
    marginLeft: DRAWER_COLLAPSED,
    width: `calc(100% - ${DRAWER_COLLAPSED}px)`,
  },
  variants: [
    {
      props: ({ open }) => open,
      style: {
        [theme.breakpoints.up("md")]: {
          marginLeft: DRAWER_WIDTH,
          width: `calc(100% - ${DRAWER_WIDTH}px)`,
          transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
      },
    },
  ],
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (p) => p !== "open",
})(({ theme }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
}));

const NAV_GROUPS = [
  {
    labelKey: null,
    items: [
      { labelKey: "dashboard",      icon: LayoutDashboard, view: "overview",       requiresProfile: false },
      { labelKey: "calls",          icon: PhoneCall,        view: "callhistory",    requiresProfile: false },
      { labelKey: "contacts",       icon: Users,            view: "representatives",requiresProfile: false, hasArrow: true },
      { labelKey: "crm",            icon: Ticket,           view: "crm",            requiresProfile: false },
      { labelKey: "orders",         icon: ClipboardList,    view: "orders",         requiresProfile: false },
      { labelKey: "catalog",        icon: Package,          view: "catalog",        requiresProfile: false },
      { labelKey: "calendar",       icon: CalendarRange,    view: "calendar",       requiresProfile: false },
      { labelKey: "voicemails",     icon: Voicemail,        view: "voicemail",      requiresProfile: false, hasArrow: true },
      { labelKey: "analytics",      icon: BarChart3,        view: "analytics",      requiresProfile: true  },
      { labelKey: "callFlowBuilder",icon: GitBranch,        view: "callflow",       requiresProfile: true  },
      { labelKey: "whatsapp",       icon: MessagesSquare,   view: "whatsapp",       requiresProfile: false },
    ],
  },
  {
    labelKey: "system",
    items: [
      { labelKey: "settings", icon: Settings, view: "profile", requiresProfile: false },
    ],
  },
];

// Hidden config items surfaced via Settings expansion — each maps to an existing view
const SETTINGS_SUB = [
  { labelKey: "profile",      icon: User,           view: "profile"      },
  { labelKey: "business",     icon: Blocks,         view: "business"     },
  { labelKey: "agents",       icon: Headset,         view: "agents"       },
  { labelKey: "phoneNumbers", icon: Phone,           view: "phonenumbers" },
  { labelKey: "dispositions", icon: Tag,             view: "dispositions" },
  { labelKey: "performance",  icon: Gauge,           view: "performance"  },
  { labelKey: "transfers",    icon: ArrowLeftRight,  view: "transfers"    },
  { labelKey: "emailReports", icon: Mail,            view: "emailreports" },
  { labelKey: "billing",      icon: CreditCardIcon,  view: "billing"      },
  { labelKey: "aiUsage",     icon: Brain,            view: "aiusage"      },
  { labelKey: "knowledge",   icon: BookOpen,          view: "knowledge"    },
  { labelKey: "agentPersona", icon: Bot,             view: "persona"      },
  { labelKey: "aiActions",   icon: Zap,              view: "aiactions"    },
  { labelKey: "orderSettings", icon: SlidersHorizontal, view: "ordersettings" },
];

// Subaccount nav — perm:null means always visible, otherwise must be in user.permissions
// agentPermKey: key in agentPerms object that must be truthy for item to appear
const SUBACCOUNT_NAV = [
  { labelKey: "myDashboard", icon: LayoutDashboard, view: "agent",          perm: null,        agentPermKey: null       },
  { labelKey: "analytics",   icon: BarChart3,        view: "analytics",      perm: "analytics", agentPermKey: null       },
  { labelKey: "crm",         icon: Ticket,           view: "crm",            perm: "view",      agentPermKey: null       },
  { labelKey: "orders",      icon: ClipboardList,    view: "orders",         perm: "view",      agentPermKey: null       },
  { labelKey: "catalog",     icon: Package,          view: "catalog",        perm: "view",      agentPermKey: null       },
  { labelKey: "whatsapp",    icon: MessagesSquare,   view: "whatsapp",       perm: null,        agentPermKey: "whatsapp" },
  { labelKey: "contacts",    icon: Users,            view: "representatives",perm: "edit",      agentPermKey: null       }, // the customers (4.8f.3c: agents moved to Settings)
  { labelKey: "voicemail",   icon: Voicemail,        view: "voicemail",      perm: null,        agentPermKey: null       },
  { labelKey: "profile",     icon: User,             view: "profile",        perm: null,        agentPermKey: null       },
];

const NAV_VIEW_KEYS = {
  overview: "dashboard", callhistory: "calls", representatives: "contacts",
  crm: "crm", voicemail: "voicemails", emailreports: "emailReports",
  analytics: "analytics", callflow: "callFlowBuilder", agents: "agents",
  profile: "settings", billing: "billing",
  phonenumbers: "phoneNumbers", whatsapp: "whatsapp",
  dispositions: "dispositions", performance: "performance", transfers: "transfers",
  aiusage: "aiUsage", orders: "orders", ordersettings: "orderSettings", catalog: "catalog", business: "business",
  knowledge: "knowledge", persona: "agentPersona", aiactions: "aiActions",
  calendar: "calendar",
};

const isSupervisor = (permsSet) =>
  permsSet.has("analytics") || permsSet.has("edit");

const roleChipConfig = (isSubAccount, permsSet, plan) => {
  if (!isSubAccount) return { label: plan || "Starter", color: "#7A3FF2" };
  if (isSupervisor(permsSet)) return { label: "Supervisor", color: "#3B82F6" };
  return { label: "Agent", color: "#22C55E" };
};

const SideBar = () => {
  const theme = useTheme();
  const { mode, toggleColorMode } = useColorMode();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  usePersistLanguage();
  const { t } = useTranslation(["nav", "common"]);
  const user = useSelector((s) => s.auth.user);
  const userDetails = useSelector((s) => s.auth.userDetails);
  const profile = useSelector((s) => s.profile.profile);
  const profileStatus = useSelector((s) => s.profile.status);
  const toast = useSelector((s) => s.toast);
  const flowMode = useSelector((s) => s.flowBuilder?.flowMode || "cards");
  const sections  = useSelector((s) => s.sections?.sections || []);
  const languages = useSelector((s) => s.profile?.profile?.languages || ["en"]);
  const timezone  = useSelector((s) => s.profile?.profile?.timezone  || "UTC");
  const analyticsSummary = useSelector((s) => s.analytics.liveSummary);
  const crmOpenCount = useSelector((s) => s.crm.openCount);
  const mainRef = React.useRef(null);
  const [open, setOpen] = React.useState(true);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [activeView, setActiveView] = React.useState(() => {
    try {
      const saved = localStorage.getItem("trd_activeView");
      if (saved) return saved;
    } catch {}
    return user?.accountType === "subaccount" ? "agent" : "overview";
  });

  // Persist active view so refresh returns to the same page
  React.useEffect(() => {
    try { localStorage.setItem("trd_activeView", activeView); } catch {}
  }, [activeView]);

  // Honor ?crmView= deep links — if URL contains crmView param, activate CRM section. ?view= opens one of the account owner's
  // billing pages — where Stripe's payment page sends the business back (B1.2).
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('crmView')) setActiveView('crm');
      const view = params.get('view');
      // …and the weekly digest's link (4.6c): ?view=analytics&tab=digest (Analytics reads its tab from the same address).
      if (view && ["aiusage", "billing", "analytics"].includes(view) && user?.accountType !== "subaccount") setActiveView(view);
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // On mobile the drawer is always fully expanded when open; on desktop it collapses to icon width
  const showLabels = isMobile || open;

  const isSubAccount      = user?.accountType === "subaccount";
  const isProfileComplete = !!(profile?.languages?.some(l => profile?.locales?.[l]?.name?.trim()));
  const agentPerms = useSelector(selectAgentPerms);

  const permsSet  = new Set((user?.permissions || "").split(",").map((p) => p.trim()).filter(Boolean));
  const hasPerm   = (p) => p === null || permsSet.has(p);
  const hasAgentPerm = (k) => k === null || agentPerms?.[k] === true;
  // WHICH HOMES THIS BUSINESS USES (S7). Until it has been read every home shows — what every tenant saw before.
  const business = useBusiness(user?.id);
  const modules = business?.modules;
  const subNavItems = SUBACCOUNT_NAV.filter((item) => hasPerm(item.perm) && hasAgentPerm(item.agentPermKey) && viewAllowed(item.view, modules));
  // NEW ORDERS NOBODY HAS SEEN YET, beside "Orders" on every page — and their sound where the board is not open
  // (S6 6e). Polled only for an account that can see the board, of a business that takes orders.
  const ordersUnseen = useUnseenOrders(user?.id, {
    enabled: !!user && viewAllowed("orders", modules) && (!isSubAccount || subNavItems.some((item) => item.view === "orders")),
    boardOpen: activeView === "orders",
  });

  // WHATSAPP CHATS WAITING ON A PERSON (4.5, the owner's decision 3): the badge beside "WhatsApp" on every page, for an account
  // that sees the inbox. The inbox itself chimes while it is open.
  const { waiting: waWaiting } = useWaitingHandoffs({
    enabled: !!user && (!isSubAccount || subNavItems.some((item) => item.view === "whatsapp")),
  });

  React.useEffect(() => {
    if (isSubAccount && agentPerms === null) dispatch(fetchAgentPerms());
  }, [isSubAccount]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    dispatch(fetchMe());
    dispatch(getProfile());
    dispatch(fetchLiveSummary());
    dispatch(fetchOpenCount());
  }, [dispatch]);

  React.useEffect(() => {
    if (!userDetails) return;
    if (!profile?.locales?.en?.name?.trim() && userDetails.enCompanyName)
      dispatch(setLocaleName({ lang: "en", value: userDetails.enCompanyName }));
    if (!profile?.locales?.ar?.name?.trim() && userDetails.arCompanyName)
      dispatch(setLocaleName({ lang: "ar", value: userDetails.arCompanyName }));
  }, [userDetails]);

  const [showWizard, setShowWizard] = React.useState(false);

  const initialViewSet = React.useRef(false);
  React.useEffect(() => {
    if (isSubAccount) { initialViewSet.current = true; return; }
    if (profileStatus === "Success" && !initialViewSet.current) {
      initialViewSet.current = true;
      if (!isProfileComplete && !localStorage.getItem("wizardSeen")) {
        setShowWizard(true);
      }
    }
  }, [profileStatus, isProfileComplete, isSubAccount]);

  const handleLogout = async () => {
    await unregisterSIP();
    dispatch(logout());
    navigate("/signin");
  };

  const todayKey = new Date().toISOString().slice(0, 10);
  const todayTrend = analyticsSummary?.dailyTrend?.find((d) => d.date === todayKey);
  const todayMissed = todayTrend ? todayTrend.total - todayTrend.answered : 0;

  // Poll for fresh badge data every 60 s
  React.useEffect(() => {
    const id = setInterval(() => {
      dispatch(fetchLiveSummary());
      dispatch(fetchOpenCount());
    }, 60_000);
    return () => clearInterval(id);
  }, [dispatch]);

  // Toast when missed-call count rises between polls
  const prevMissedRef = React.useRef(null);
  React.useEffect(() => {
    if (!analyticsSummary) return;
    if (prevMissedRef.current === null) {
      prevMissedRef.current = todayMissed;
      return;
    }
    if (todayMissed > prevMissedRef.current) {
      const diff = todayMissed - prevMissedRef.current;
      dispatch(showToast({
        message: `${diff} new missed call${diff > 1 ? 's' : ''} today — check call history`,
        severity: 'warning',
      }));
    }
    prevMissedRef.current = todayMissed;
  }, [analyticsSummary, todayMissed, dispatch]);

  const initials = userDetails
    ? `${userDetails.firstName?.[0] || ""}${userDetails.lastName?.[0] || ""}`.toUpperCase()
    : user?.name?.[0]?.toUpperCase() || "U";

  const iconBtnSx = {
    width: 34, height: 34,
    borderRadius: "var(--r-sm)",
    color: "var(--ink-500)",
    background: "transparent",
    border: "1px solid var(--line-200)",
    "&:hover": {
      background: "var(--surface-2)",
      color: "var(--ink-800)",
    },
  };

  // Support dialog
  const [supportOpen,    setSupportOpen]    = React.useState(false);
  const [supportSubject, setSupportSubject] = React.useState("");
  const [supportMsg,     setSupportMsg]     = React.useState("");
  const [supportSending, setSupportSending] = React.useState(false);
  const [supportSent,    setSupportSent]    = React.useState(false);
  const [supportErr,     setSupportErr]     = React.useState("");

  const handleSupportOpen = () => {
    setSupportOpen(true);
    setSupportSent(false);
    setSupportErr("");
  };

  const handleSupportSend = async () => {
    if (!supportSubject.trim() || !supportMsg.trim()) {
      setSupportErr("Please fill in both subject and message.");
      return;
    }
    setSupportSending(true);
    setSupportErr("");
    try {
      await axiosInstance.post("/support/contact", { subject: supportSubject.trim(), message: supportMsg.trim() });
      setSupportSent(true);
      setSupportSubject("");
      setSupportMsg("");
    } catch (e) {
      setSupportErr(e.response?.data?.message || "Failed to send. Please try again.");
    } finally {
      setSupportSending(false);
    }
  };

  // Notification bell
  const [bellAnchor, setBellAnchor] = React.useState(null);
  const totalAlerts = (todayMissed || 0) + (crmOpenCount || 0);

  // Track unseen count — count items accumulated since bell was last opened
  const [lastSeenTotal, setLastSeenTotal] = React.useState(() => {
    try { return parseInt(localStorage.getItem("notif_seen") || "-1", 10); }
    catch { return -1; }
  });
  const unseenCount = lastSeenTotal < 0 ? totalAlerts : Math.max(0, totalAlerts - lastSeenTotal);

  const handleBellOpen = (e) => {
    setBellAnchor(e.currentTarget);
    setLastSeenTotal(totalAlerts);
    try { localStorage.setItem("notif_seen", String(totalAlerts)); } catch {}
  };

  const renderItem = (labelKey, Icon, view, requiresProfile, badge, hasArrow) => {
    const label = t("nav:" + labelKey);
    const disabled = !isSubAccount && requiresProfile && !isProfileComplete;
    const isActive = activeView === view && !(badge === "New" && activeView !== view);
    const tooltipText = disabled
      ? "Fill your company names in Profile to unlock this"
      : showLabels ? "" : label;

    return (
      // Keyed by view, not the translated label: different keys can share a
      // translation (voicemail/voicemails collide in Arabic), and duplicate
      // keys break React reconciliation.
      <ListItem key={view || labelKey} disablePadding sx={{ display: "block", mb: 0.25 }}>
        <Tooltip title={tooltipText} placement="right" arrow disableHoverListener={showLabels && !disabled}>
          <span>
            <ListItemButton
              selected={isActive}
              disabled={disabled}
              onClick={() => {
                // When leaving the call flow tab, reset mode so next visit shows the flow list
                if (activeView === "callflow" && view !== "callflow") {
                  dispatch(setFlowModeLocal("landing"));
                }
                setActiveView(view);
                if (isMobile) setOpen(false);
              }}
              sx={{
                mx: 0.75,
                width: "calc(100% - 12px)",
                minHeight: 40,
                borderRadius: "var(--r-md)",
                justifyContent: showLabels ? "flex-start" : "center",
                px: showLabels ? 1.5 : 0,
                background: isActive ? "var(--grad-brand-135)" : "transparent",
                boxShadow: isActive ? "var(--shadow-cta)" : "none",
                transition: "background .15s, box-shadow .15s",
                "&:hover": {
                  background: isActive ? "var(--grad-brand-135)" : "rgba(255,255,255,0.07)",
                },
                "&.Mui-selected": {
                  background: "var(--grad-brand-135)",
                  "&:hover": { background: "var(--grad-brand-135)" },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: showLabels ? 1.5 : 0,
                  justifyContent: "center",
                  color: isActive ? "#fff" : disabled ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.60)",
                  transition: "color 0.15s",
                }}
              >
                {(() => {
                  const badgeSx = {
                    "& .MuiBadge-badge": { fontSize: "0.6rem", height: 15, minWidth: 15, padding: "0 3px" },
                  };
                  if (view === "analytics" && todayMissed > 0)
                    return <Badge badgeContent={todayMissed} color="error" max={99} sx={badgeSx}><Icon size={18} /></Badge>;
                  if (view === "crm" && crmOpenCount > 0)
                    return <Badge badgeContent={crmOpenCount} color="warning" max={99} sx={badgeSx}><Icon size={18} /></Badge>;
                  if (view === "orders" && ordersUnseen > 0 && activeView !== "orders")
                    return <Badge badgeContent={ordersUnseen} color="error" max={99} sx={badgeSx}><Icon size={18} /></Badge>;
                  if (view === "whatsapp" && waWaiting > 0)
                    return <Badge badgeContent={waWaiting} color="error" max={99} sx={badgeSx}><Icon size={18} /></Badge>;
                  return <Icon size={18} />;
                })()}
              </ListItemIcon>
              {showLabels && (
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{
                    fontFamily: "var(--font-ui)",
                    fontSize: "0.875rem",
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? "#fff" : disabled ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.72)",
                  }}
                />
              )}
              {showLabels && badge && (
                <Box sx={{
                  px: "6px", py: "1px", borderRadius: "6px",
                  background: "linear-gradient(135deg,#1FC0DA,#7A3FE4)",
                  fontSize: "0.5625rem", fontWeight: 700, color: "#fff",
                  lineHeight: 1.6, letterSpacing: "0.02em", flexShrink: 0,
                }}>
                  {badge}
                </Box>
              )}
              {showLabels && hasArrow && !badge && (
                <ChevronDown size={12} color="rgba(255,255,255,0.35)" style={{ flexShrink: 0 }} />
              )}
            </ListItemButton>
          </span>
        </Tooltip>
      </ListItem>
    );
  };

  return (
    // The phone, for anyone with one (Stage 4.8f.4b): held above every page, so moving between pages never drops a call.
    <PhoneProvider>
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* Skip-to-content link — JS-handled so #main-content never needs tabIndex={-1}
          permanently. A static tabIndex would let the element hold focus while MUI
          aria-hides #root (when a Drawer/Popover opens), which Chrome blocks with a
          console warning. Instead we focus the first natural child on click and only
          transiently grant tabIndex as a fallback. */}
      <Box
        component="a"
        href="#main-content"
        onClick={(e) => {
          e.preventDefault();
          const main = mainRef.current;
          if (!main) return;
          const sel = 'button:not([disabled]),a[href],[tabindex]:not([tabindex="-1"]),input:not([disabled]),select:not([disabled]),textarea:not([disabled])';
          const first = main.querySelector(sel);
          if (first) {
            first.focus();
          } else {
            main.setAttribute("tabindex", "-1");
            main.focus({ preventScroll: false });
            main.addEventListener("blur", () => main.removeAttribute("tabindex"), { once: true });
          }
        }}
        sx={{
          position: "absolute", top: -40, left: 8, zIndex: 9999,
          px: 2, py: 0.75, borderRadius: 1, fontWeight: 700, fontSize: "0.875rem",
          background: "#7A3FF2", color: "#fff",
          "&:focus": { top: 8 },
          transition: "top 0.15s",
        }}
      >
        Skip to content
      </Box>

      {/* ── Top AppBar ─────────────────────────────────── */}
      <AppBar position="fixed" open={open} elevation={0}>
        <Toolbar sx={{ minHeight: 64, gap: "10px", px: { xs: 2, md: 2.5 } }}>

          {/* Page title */}
          <Typography sx={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: "0.9375rem",
            color: "var(--ink-800)",
            letterSpacing: "-0.01em",
            display: { xs: "none", sm: "block" },
            flexShrink: 0,
          }}>
            {t("nav:" + (NAV_VIEW_KEYS[activeView] || "dashboard"))}
          </Typography>

          {/* Search pill */}
          <Box sx={{
            flex: 1,
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: "8px",
            mx: "16px",
            maxWidth: 360,
            height: 36,
            borderRadius: "10px",
            border: "1px solid var(--line-200)",
            background: "var(--surface-page)",
            px: "12px",
            cursor: "text",
          }}>
            <Search size={14} color="var(--ink-400)" style={{ flexShrink: 0 }} />
            <InputBase
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search anything…"
              sx={{
                flex: 1,
                fontSize: "0.8125rem",
                color: "var(--ink-600)",
                fontFamily: "var(--font-ui)",
                "& input::placeholder": { color: "var(--ink-400)", opacity: 1 },
              }}
            />
          </Box>

          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            {/* Wallboard */}
            {!isSubAccount && (
              <Tooltip title={t("openWallboard")} arrow>
                <IconButton size="small" aria-label={t("openWallboardNewTab")} sx={iconBtnSx}
                  onClick={() => window.open(`${window.location.origin}/heycallcenter/wallboard`, "_blank")}>
                  <Monitor size={16} />
                </IconButton>
              </Tooltip>
            )}

            {/* The phone: opens / minimises the docked phone, and says when a call is on (4.8f.4b) */}
            <PhoneTopButton />

            {/* Notification bell */}
            <Tooltip title={t("notifications")} arrow>
              <IconButton size="small" aria-label={unseenCount ? t("notificationsAriaWithCount", { count: unseenCount }) : t("notifications")}
                sx={iconBtnSx} onClick={handleBellOpen}>
                <Badge badgeContent={unseenCount || null} color="error" max={99}
                  sx={{ "& .MuiBadge-badge": { fontSize: "0.6rem", height: 15, minWidth: 15, padding: "0 3px" } }}>
                  <Bell size={16} />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Dark mode toggle */}
            <IconButton onClick={toggleColorMode} size="small"
              aria-label={mode === "light" ? t("switchToDarkMode") : t("switchToLightMode")}
              sx={iconBtnSx}>
              {mode === "light" ? <Moon size={16} /> : <Sun size={16} />}
            </IconButton>

            {/* User area */}
            <Box
              onClick={() => setActiveView("profile")}
              role="button"
              tabIndex={0}
              aria-label={t("goToProfile")}
              onKeyDown={(e) => e.key === "Enter" && setActiveView("profile")}
              sx={{
                display: "flex", alignItems: "center", gap: "8px",
                pl: "6px", pr: "10px", py: "4px",
                borderRadius: "10px",
                border: "1px solid var(--line-200)",
                background: "var(--surface-card)",
                cursor: "pointer",
                "&:hover": { borderColor: "var(--line-300)", background: "var(--surface-2)" },
                transition: "all 0.15s",
              }}
            >
              <Avatar sx={{
                width: 28, height: 28, fontSize: "0.7rem", fontWeight: 700,
                background: BRAND_GRADIENT, boxShadow: "0 2px 6px rgba(122,63,242,0.28)",
              }}>
                {initials}
              </Avatar>
              <Box sx={{ display: { xs: "none", sm: "block" }, lineHeight: 1 }}>
                <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--ink-800)", fontFamily: "var(--font-ui)", lineHeight: 1.2 }}>
                  {userDetails?.firstName || user?.name?.split(" ")[0] || "User"}
                </Typography>
                <Typography sx={{ fontSize: "0.6875rem", color: "var(--ink-400)", fontFamily: "var(--font-ui)" }}>
                  {isSubAccount ? (isSupervisor(permsSet) ? t("nav:supervisor") : t("nav:agent")) : "Admin"}
                </Typography>
              </Box>
              <ChevronDown size={13} color="var(--ink-400)" />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ── Notification popover ──────────────────────────── */}
      <Popover
        open={Boolean(bellAnchor)}
        anchorEl={bellAnchor}
        onClose={() => { if (document.activeElement instanceof HTMLElement) document.activeElement.blur(); setBellAnchor(null); }}
        disableAutoFocus
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top",    horizontal: "right" }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1,
            width: 320,
            borderRadius: "var(--r-lg)",
            border: "1px solid var(--line-200)",
            background: "var(--surface-card)",
            boxShadow: "var(--shadow-lg)",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2.5, pt: 2, pb: 1.25, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontFamily: "var(--font-display)", fontSize: "0.875rem", fontWeight: 700, color: "var(--ink-900)" }}>
            Notifications
          </Typography>
          {totalAlerts > 0 && (
            <Chip
              label={`${totalAlerts} active`}
              size="small"
              sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700, background: alpha("#F59E0B", 0.1), color: "#F59E0B", border: "none", boxShadow: "none" }}
            />
          )}
        </Box>

        <Divider />

        <Box sx={{ py: 1 }}>
          {/* Missed calls row */}
          {todayMissed > 0 ? (
            <Box
              component="button"
              onClick={() => { setActiveView("callhistory"); setBellAnchor(null); }}
              sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                width: "100%", px: 2.5, py: 1.25, border: "none",
                background: "transparent", cursor: "pointer", textAlign: "left",
                "&:hover": { background: "rgba(239,68,68,0.05)" },
                transition: "background 0.15s",
              }}
            >
              <Box sx={{ width: 32, height: 32, borderRadius: "9px", background: alpha("#EF4444", 0.1), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <PhoneMissed size={15} color="#EF4444" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: "var(--font-ui)", fontSize: "0.8125rem", fontWeight: 600, color: "var(--ink-900)", lineHeight: 1.3 }}>
                  {todayMissed} missed call{todayMissed !== 1 ? "s" : ""} today
                </Typography>
                <Typography sx={{ fontFamily: "var(--font-ui)", fontSize: "0.7rem", color: "var(--ink-400)" }}>
                  Review in Call History
                </Typography>
              </Box>
              <ArrowRight size={14} color={alpha("#EF4444", 0.7)} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 1.25 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: "9px", background: alpha("#22C55E", 0.1), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <PhoneMissed size={15} color="#22C55E" />
              </Box>
              <Typography sx={{ fontFamily: "var(--font-ui)", fontSize: "0.8125rem", color: "var(--ink-500)" }}>
                No missed calls today
              </Typography>
            </Box>
          )}

          {/* CRM tickets row */}
          {crmOpenCount > 0 ? (
            <Box
              component="button"
              onClick={() => { setActiveView("crm"); setBellAnchor(null); }}
              sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                width: "100%", px: 2.5, py: 1.25, border: "none",
                background: "transparent", cursor: "pointer", textAlign: "left",
                "&:hover": { background: "rgba(245,158,11,0.05)" },
                transition: "background 0.15s",
              }}
            >
              <Box sx={{ width: 32, height: 32, borderRadius: "9px", background: alpha("#F59E0B", 0.1), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Ticket size={15} color="#F59E0B" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: "var(--font-ui)", fontSize: "0.8125rem", fontWeight: 600, color: "var(--ink-900)", lineHeight: 1.3 }}>
                  {crmOpenCount} open ticket{crmOpenCount !== 1 ? "s" : ""}
                </Typography>
                <Typography sx={{ fontFamily: "var(--font-ui)", fontSize: "0.7rem", color: "var(--ink-400)" }}>
                  Review in CRM
                </Typography>
              </Box>
              <ArrowRight size={14} color={alpha("#F59E0B", 0.7)} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 1.25 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: "9px", background: alpha("#22C55E", 0.1), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Ticket size={15} color="#22C55E" />
              </Box>
              <Typography sx={{ fontFamily: "var(--font-ui)", fontSize: "0.8125rem", color: "var(--ink-500)" }}>
                No open tickets
              </Typography>
            </Box>
          )}
        </Box>

        {totalAlerts === 0 && (
          <>
            <Divider />
            <Box sx={{ px: 2.5, py: 1.5, textAlign: "center" }}>
              <Typography sx={{ fontFamily: "var(--font-ui)", fontSize: "0.75rem", color: "var(--ink-400)" }}>All clear — nothing needs attention</Typography>
            </Box>
          </>
        )}
      </Popover>

      {/* ── Help & Support dialog ────────────────────────── */}
      <Dialog open={supportOpen} onClose={() => setSupportOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: "var(--r-xl)", background: "var(--surface-card)" } }}>
        <DialogTitle sx={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "1rem", pb: 0.5 }}>
          Contact Support
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          {supportSent ? (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <Typography sx={{ fontSize: "2rem", mb: 1 }}>✅</Typography>
              <Typography sx={{ fontWeight: 700, color: "var(--ink-900)", mb: 0.5 }}>{t("messageSent")}</Typography>
              <Typography sx={{ fontSize: "0.875rem", color: "var(--ink-500)" }}>
                {t("supportTeamReply")}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {supportErr && (
                <Alert severity="error" sx={{ borderRadius: "var(--r-md)", fontSize: "0.8125rem" }}>{supportErr}</Alert>
              )}
              <TextField
                label={t("supportSubjectLabel")}
                size="small"
                fullWidth
                value={supportSubject}
                onChange={(e) => setSupportSubject(e.target.value)}
                placeholder={t("supportSubjectPlaceholder")}
                disabled={supportSending}
                inputProps={{ maxLength: 150 }}
              />
              <TextField
                label={t("supportMessageLabel")}
                size="small"
                fullWidth
                multiline
                rows={5}
                value={supportMsg}
                onChange={(e) => setSupportMsg(e.target.value)}
                placeholder="Describe your issue or question in detail…"
                disabled={supportSending}
                inputProps={{ maxLength: 2000 }}
              />
              <Typography sx={{ fontSize: "0.75rem", color: "var(--ink-400)" }}>
                Sending to <strong>admin@teleredo.com</strong>
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          {supportSent ? (
            <Button onClick={() => setSupportOpen(false)} variant="contained"
              sx={{ background: "var(--grad-brand-135)", borderRadius: "var(--r-md)", fontWeight: 700 }}>
              Close
            </Button>
          ) : (
            <>
              <Button onClick={() => setSupportOpen(false)} disabled={supportSending}
                sx={{ color: "var(--ink-500)", fontWeight: 600 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSupportSend}
                disabled={supportSending || !supportSubject.trim() || !supportMsg.trim()}
                startIcon={supportSending ? <CircularProgress size={14} color="inherit" /> : <Send size={14} />}
                sx={{ background: "var(--grad-brand-135)", borderRadius: "var(--r-md)", fontWeight: 700 }}
              >
                {supportSending ? "Sending…" : "Send Message"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Left Drawer ─────────────────────────────────── */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            background: SIDEBAR_BG,
            borderInlineEnd: "none",
            boxShadow: "4px 0 24px rgba(18,20,46,.16)",
            ...(isMobile && { width: DRAWER_WIDTH, boxSizing: "border-box" }),
          },
        }}
      >
        <DrawerHeader
          sx={{
            justifyContent: showLabels ? "space-between" : "center",
            px: showLabels ? 2 : 0,
            minHeight: "64px !important",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            {/* Glyph in gradient rounded-square — always visible */}
            <Box sx={{
              width: 34, height: 34, borderRadius: "10px", flexShrink: 0,
              background: "linear-gradient(135deg,#1FC0DA,#4B78EC 50%,#7A3FE4)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 4px 12px rgba(122,63,228,.35)",
            }}>
              <Box component="img" src={glyphImg} alt="Teleredo"
                sx={{ height: 20, width: "auto", filter: "brightness(0) invert(1)" }} />
            </Box>
            {showLabels && (
              <Typography sx={{
                fontFamily: "var(--font-display)", fontWeight: 800,
                fontSize: "0.9375rem", color: "#fff",
                letterSpacing: "-0.02em", lineHeight: 1,
              }}>
                Teleredo
              </Typography>
            )}
          </Box>
          {/* Collapse button only shown when expanded — AppBar hamburger handles expand */}
          {showLabels && (
            <IconButton
              onClick={() => setOpen((o) => isMobile ? false : !o)}
              size="small"
              aria-label={isMobile ? "Close navigation" : "Collapse sidebar"}
              sx={{
                width: 30, height: 30, borderRadius: "9px",
                color: "rgba(255,255,255,0.55)",
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.10)",
                "&:hover": { background: "rgba(255,255,255,0.12)", color: "#fff" },
              }}
            >
              {isMobile
                ? <ChevronLeft size={16} />
                : theme.direction === "rtl" ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </IconButton>
          )}
        </DrawerHeader>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

        {showLabels && isSubAccount && (
          <Typography sx={{
            px: 2, pt: 1.5, pb: 0.5,
            fontSize: "0.625rem", fontWeight: 700,
            letterSpacing: "0.09em", textTransform: "uppercase",
            color: "rgba(255,255,255,0.35)",
          }}>
            {isSupervisor(permsSet) ? t("nav:supervisor") : t("nav:agentWorkspace")}
          </Typography>
        )}

        <List sx={{ px: 0.5, pt: showLabels ? 0.5 : 1, flex: 1, overflowY: "auto" }}>
          {isSubAccount
            ? subNavItems.map(({ labelKey, icon: Icon, view }) => renderItem(labelKey, Icon, view, false, null, false))
            : NAV_GROUPS.map((group, gi) => (
                <React.Fragment key={group.labelKey ?? "root"}>
                  {gi > 0 && (
                    showLabels
                      ? (
                        <Typography sx={{
                          px: 2, pt: 1.5, pb: 0.5,
                          fontSize: "0.625rem", fontWeight: 700,
                          letterSpacing: "0.09em", textTransform: "uppercase",
                          color: "rgba(255,255,255,0.35)",
                        }}>
                          {group.labelKey ? t("nav:" + group.labelKey) : null}
                        </Typography>
                      )
                      : <Divider sx={{ my: 0.75, mx: 1.5, borderColor: "rgba(255,255,255,0.08)" }} />
                  )}
                  {group.items.filter((item) => viewAllowed(item.view, modules)).map(({ labelKey, icon: Icon, view, requiresProfile, badge, hasArrow }) => {
                    // Settings item gets special expand/collapse behaviour
                    if (labelKey === "settings" && !isSubAccount) {
                      const isSettingsActive = SETTINGS_SUB.some((s) => s.view === activeView);
                      return (
                        <React.Fragment key="settings-group">
                          <ListItem disablePadding sx={{ display: "block", mb: 0.25 }}>
                            <ListItemButton
                              onClick={() => setSettingsOpen((o) => !o)}
                              sx={{
                                mx: 0.75, width: "calc(100% - 12px)", minHeight: 40,
                                borderRadius: "var(--r-md)",
                                justifyContent: showLabels ? "flex-start" : "center",
                                px: showLabels ? 1.5 : 0,
                                background: isSettingsActive ? "var(--grad-brand-135)" : "transparent",
                                "&:hover": { background: isSettingsActive ? "var(--grad-brand-135)" : "rgba(255,255,255,0.07)" },
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 0, mr: showLabels ? 1.5 : 0, justifyContent: "center", color: isSettingsActive ? "#fff" : "rgba(255,255,255,0.60)" }}>
                                <Icon size={18} />
                              </ListItemIcon>
                              {showLabels && (
                                <ListItemText primary={t("nav:settings")} primaryTypographyProps={{ fontFamily: "var(--font-ui)", fontSize: "0.875rem", fontWeight: isSettingsActive ? 600 : 400, color: isSettingsActive ? "#fff" : "rgba(255,255,255,0.72)" }} />
                              )}
                              {showLabels && (
                                <ChevronDown size={12} color="rgba(255,255,255,0.35)" style={{ transform: settingsOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }} />
                              )}
                            </ListItemButton>
                          </ListItem>
                          {settingsOpen && showLabels && SETTINGS_SUB.filter((s) => viewAllowed(s.view, modules)).map(({ labelKey: sk, icon: SI, view: sv }) => (
                            <ListItem key={sk} disablePadding sx={{ display: "block", mb: 0.15 }}>
                              <ListItemButton
                                selected={activeView === sv}
                                onClick={() => { setActiveView(sv); if (isMobile) setOpen(false); }}
                                sx={{
                                  mx: 0.75, ml: 2.5, width: "calc(100% - 32px)", minHeight: 34,
                                  borderRadius: "var(--r-md)", px: 1.5,
                                  background: activeView === sv ? "rgba(255,255,255,0.12)" : "transparent",
                                  "&:hover": { background: "rgba(255,255,255,0.07)" },
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 0, mr: 1.25, justifyContent: "center", color: activeView === sv ? "#fff" : "rgba(255,255,255,0.50)" }}>
                                  <SI size={15} />
                                </ListItemIcon>
                                <ListItemText primary={t("nav:" + sk)} primaryTypographyProps={{ fontFamily: "var(--font-ui)", fontSize: "0.8125rem", fontWeight: activeView === sv ? 600 : 400, color: activeView === sv ? "#fff" : "rgba(255,255,255,0.60)" }} />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </React.Fragment>
                      );
                    }
                    return renderItem(labelKey, Icon, view, requiresProfile, badge, hasArrow);
                  })}
                </React.Fragment>
              ))
          }
        </List>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

        <List sx={{ px: 0.5, py: 1 }}>
          {/* Help & Support */}
          <ListItem disablePadding sx={{ display: "block", mb: 0.25 }}>
            <Tooltip title={showLabels ? "" : t("nav:helpSupport")} placement="right" arrow disableHoverListener={showLabels}>
              <ListItemButton
                onClick={handleSupportOpen}
                sx={{
                  mx: 0.75, width: "calc(100% - 12px)", minHeight: 40,
                  borderRadius: "var(--r-md)",
                  justifyContent: showLabels ? "flex-start" : "center",
                  px: showLabels ? 1.5 : 0,
                  background: "transparent",
                  transition: "background .15s",
                  "&:hover": { background: "rgba(255,255,255,0.07)" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, mr: showLabels ? 1.5 : 0, justifyContent: "center", color: "rgba(255,255,255,0.50)" }}>
                  <HelpCircle size={18} />
                </ListItemIcon>
                {showLabels && (
                  <ListItemText primary={t("nav:helpSupport")} primaryTypographyProps={{ fontFamily: "var(--font-ui)", fontSize: "0.875rem", fontWeight: 400, color: "rgba(255,255,255,0.65)" }} />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>

          {user?.role === "admin" && (
            <ListItem disablePadding sx={{ display: "block", mb: 0.25 }}>
              <Tooltip title={showLabels ? "" : t("nav:adminPanel")} placement="right" arrow disableHoverListener={showLabels}>
                <ListItemButton
                  onClick={() => { if (isMobile) setOpen(false); navigate("/admin"); }}
                  sx={{
                    mx: 0.75,
                    width: "calc(100% - 12px)",
                    minHeight: 40,
                    borderRadius: "var(--r-md)",
                    justifyContent: showLabels ? "flex-start" : "center",
                    px: showLabels ? 1.5 : 0,
                    background: "transparent",
                    transition: "background .15s",
                    "&:hover": { background: "rgba(139,92,246,0.12)" },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: showLabels ? 1.5 : 0, justifyContent: "center", color: "#8B5CF6" }}>
                    <Shield size={18} />
                  </ListItemIcon>
                  {showLabels && (
                    <ListItemText
                      primary={t("nav:adminPanel")}
                      primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 600, color: "#8B5CF6" }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          )}

          <ListItem disablePadding sx={{ display: "block" }}>
            <Tooltip title={showLabels ? "" : t("nav:logout")} placement="right" arrow disableHoverListener={showLabels}>
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  mx: 0.75,
                  width: "calc(100% - 12px)",
                  minHeight: 40,
                  borderRadius: "var(--r-md)",
                  justifyContent: showLabels ? "flex-start" : "center",
                  px: showLabels ? 1.5 : 0,
                  background: "transparent",
                  transition: "background .15s",
                  "&:hover": { background: "rgba(239,68,68,0.12)" },
                }}
              >
                <ListItemIcon
                  sx={{ minWidth: 0, mr: showLabels ? 1.5 : 0, justifyContent: "center", color: "#EF4444" }}
                >
                  <LogOut size={18} />
                </ListItemIcon>
                {showLabels && (
                  <ListItemText
                    primary={t("nav:logout")}
                    primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 600, color: "#EF4444" }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        </List>

        <Box sx={{ px: showLabels ? 1.5 : 0.5, pb: 0.5, display: "flex", justifyContent: showLabels ? "flex-start" : "center" }}>
          <LanguageSwitcherCompact />
        </Box>

        {showLabels && (
          <Box sx={{ px: 2.5, pb: 2 }}>
            <Typography sx={{ color: "rgba(255,255,255,0.25)", fontSize: "0.6875rem", fontFamily: "var(--font-ui)" }}>
              Teleredo v1.0
            </Typography>
          </Box>
        )}
      </Drawer>

      {/* ── Main content ───────────────────────────────── */}
      <Box
        ref={mainRef}
        component="main"
        id="main-content"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          background: "var(--surface-page)",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          outline: "none",
        }}
      >
        <DrawerHeader />

        <Box sx={{
          flex: 1,
          p: (activeView === "callflow" && flowMode !== "cards") ? 0 : { xs: 2.5, md: 3.5 },
          // Room for the docked phone on a wide screen (PhoneProvider.jsx sets --phone-dock-w; 0px when minimised or none).
          "@media (min-width:1200px)": { paddingInlineEnd: (activeView === "callflow" && flowMode !== "cards") ? "var(--phone-dock-w, 0px)" : "calc(28px + var(--phone-dock-w, 0px))" },
          overflow: (activeView === "callflow" && flowMode === "custom") ? "hidden" : "unset",
          display: (activeView === "callflow" && flowMode === "custom") ? "flex" : "block",
          flexDirection: "column",
        }}>
          {/* The account owner's billing banner (B1.3): a failed payment, the AI paused, a subscription ending. */}
          {!isSubAccount && <BillingBanner onOpenBilling={() => setActiveView("billing")} />}
          <ChunkErrorBoundary resetKey={activeView}>
          <React.Suspense fallback={<PageFallback />}>
            {/* A REMEMBERED PAGE WHOSE HOME IS NOW SWITCHED OFF is said to be, with the way back for the owner — never
                a blank page, and never the page itself. */}
            {!viewAllowed(activeView, modules) ? (
              <ModuleOff module={VIEW_MODULE[activeView]} owner={!isSubAccount} onOpen={() => setActiveView("business")} />
            ) : isSubAccount ? (
              <>
                {/* Always mounted so JsSIP UA stays registered; CSS-hidden when not active */}
                <Box sx={{ display: activeView === "agent" ? "block" : "none" }}>
                  <AgentDashboard onIncomingCall={() => setActiveView("agent")} />
                </Box>
                {activeView === "analytics"       && hasPerm("analytics") && <Analytics />}
                {activeView === "crm"             && hasPerm("view")      && <CRM />}
                {activeView === "orders"          && hasPerm("view")      && <OrdersBoard />}
                {activeView === "catalog"         && hasPerm("view")      && <CatalogPage />}
                {activeView === "representatives" && hasPerm("edit")      && <Representatives />}
                {activeView === "whatsapp"        && hasAgentPerm("whatsapp") && <WhatsAppPage />}
                {activeView === "voicemail"       && <VoicemailInbox />}
                {activeView === "profile"         && <Profile />}
              </>
            ) : (
              <>
                {activeView === "overview"        && <Overview onNavigate={setActiveView} />}
                {/* One call flow editor. A tenant whose saved mode is still the old cards value
                    lands here too, rather than on a blank page. */}
                {activeView === "callflow" && <FlowManagerPage sections={sections} languages={languages} timezone={timezone} profile={profile} />}
                {activeView === "analytics"       && <Analytics onNavigate={setActiveView} />}
                {activeView === "agents"          && <AgentsPage />}
                {activeView === "crm"             && <CRM />}
                {activeView === "representatives" && <Representatives />}
                {activeView === "dispositions"    && <Dispositions />}
                {activeView === "performance"     && <AgentPerformance />}
                {activeView === "emailreports"    && <EmailReports />}
                {activeView === "transfers"       && <TransferLogs />}
                {activeView === "callhistory"     && <CallHistory />}
                {activeView === "voicemail"       && <VoicemailInbox />}
                {activeView === "phonenumbers"    && <PhoneNumbers />}
                {activeView === "billing"         && <Billing />}
                {activeView === "profile"         && <Profile />}
                {activeView === "whatsapp"        && <WhatsAppPage />}
                {activeView === "aiusage"         && <AiUsage />}
                {activeView === "knowledge"       && <KnowledgeBase />}
                {activeView === "persona"         && <AgentPersona />}
                {activeView === "aiactions"       && <AiActions onNavigate={setActiveView} />}
                {activeView === "calendar"        && <AiCalendar />}
                {activeView === "orders"          && <OrdersBoard />}
                {activeView === "catalog"         && <CatalogPage />}
                {activeView === "ordersettings"   && <OrderSettings />}
                {activeView === "business"        && <BusinessSettings onNavigate={setActiveView} />}
              </>
            )}
          </React.Suspense>
          </ChunkErrorBoundary>
        </Box>
      </Box>

      {/* ── First-run Setup Wizard ─────────────────────── */}
      {!isSubAccount && (
        <SetupWizard
          open={showWizard}
          onComplete={(view) => {
            localStorage.setItem("wizardSeen", "1");
            setShowWizard(false);
            setActiveView(view);
          }}
          onDismiss={() => setShowWizard(false)}
        />
      )}

      {/* ── Global chatbot widget ──────────────────────── */}
      <ChatWidget />

      {/* ── Global toast ───────────────────────────────── */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => dispatch(hideToast())}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => dispatch(hideToast())}
          severity={toast.severity}
          variant="filled"
          sx={{ borderRadius: "12px", fontWeight: 600, fontSize: "0.875rem", minWidth: 260 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
    </PhoneProvider>
  );
};

export default SideBar;
